import { createRouter, createWebHistory } from 'vue-router'
import Layout from '../layout/index.vue'
import OfficeLayout from '../layout/office/index.vue'
import { useUserStore } from '../stores/user'
import { ElMessage } from 'element-plus'
import { getToken, isTokenExpired, validateToken, listenToAuthEvents, clearAllAuthState } from '@/utils/auth'
import officeRoutes from './modules/office'
import warehouseRoutes from './modules/warehouse'
// import approvalRoutes from './modules/approval'
import { hasPermission, hasRole } from '@/utils/permission'
import mobileRoutes from './mobile'

const routes = [
  {
    path: '/',
    redirect: '/office/desktop',
    meta: {
      title: '办公系统桌面'
    }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/user/profile/index.vue'),
    meta: {
      title: '个人资料',
      requiresAuth: true
    }
  },

  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/login/index.vue'),
    meta: {
      title: '登录'
    }
  },
  {
    path: '/login/callback',
    name: 'LoginCallback',
    component: () => import('../views/login/callback.vue'),
    meta: {
      title: '登录回调'
    }
  },
  // 后台管理入口
  {
    path: '/admin',
    name: 'Admin',
    component: Layout,
    meta: {
      title: '管理后台',
      requiresAuth: true
    },
    children: [
      {
        path: '',
        name: 'AdminHome',
        component: () => import('../views/admin/index.vue'),
        meta: {
          title: '管理首页',
          icon: 'desktop',
          requiresAuth: true
        }
      }
    ]
  },
  // 会员管理模块
  {
    path: '/members',
    name: 'Members',
    component: Layout,
    meta: {
      title: '会员管理',
      icon: 'user-group',
      requiresAuth: true
    },
    children: [
      {
        path: 'list',
        name: 'MemberList',
        component: () => import('../views/members/list/index.vue'),
        meta: {
          title: '会员列表',
          requiresAuth: true
        }
      },
      {
        path: 'detail/:id',
        name: 'MemberDetail',
        component: () => import('../views/members/detail/index.vue'),
        meta: {
          title: '会员详情',
          requiresAuth: true,
          hideInMenu: true
        }
      }
    ]
  },
  // 微信消息路由
  {
    path: '/wechat',
    component: Layout,
    meta: { 
      title: '微信消息', 
      icon: 'wechat',
      requiresAuth: true 
    },
    children: [
      {
        path: 'history',
        name: 'WechatHistory',
        component: () => import('../views/wechat/history.vue'),
        meta: { 
          title: '消息历史',
          requiresAuth: true
        }
      },
      {
        path: 'statistics',
        name: 'WechatStatistics',
        component: () => import('../views/wechat/statistics.vue'),
        meta: { 
          title: '消息统计',
          requiresAuth: true
        }
      },
      {
        path: 'menu',
        name: 'WechatMenu',
        component: () => import('../views/wechat/menu.vue'),
        meta: { 
          title: '菜单管理',
          requiresAuth: true
        }
      },
      {
        path: 'config',
        name: 'WechatConfig',
        component: () => import('../views/wechat/config.vue'),
        meta: { 
          title: '配置管理',
          requiresAuth: true
        }
      },
      {
        path: 'message',
        name: 'WechatMessage',
        component: () => import('../views/wechat/message.vue'),
        meta: { 
          title: '消息管理',
          requiresAuth: true
        }
      },
      {
        path: 'chat',
        name: 'WechatChat',
        component: () => import('../views/wechat/chat/index.vue'),
        meta: { 
          title: '聊天管理',
          requiresAuth: true
        }
      },
      {
        path: 'followers',
        name: 'WechatFollowers',
        component: () => import('../views/wechat/followers/index.vue'),
        meta: { 
          title: '粉丝管理',
          requiresAuth: true
        }
      }
    ]
  },
  // AI聊天路由
  {
    path: '/ai/chat',
    name: 'StandaloneAIChat',
    component: () => import('@/views/ai/chat/index.vue'),
    meta: { 
      title: 'AI聊天',
      requiresAuth: true
    }
  },

  // AI Grok 路由
  {
    path: '/ai/grok',
    name: 'StandaloneAIGrok',
    component: () => import('@/views/ai/grok/index.vue'),
    meta: {
      title: 'Grok对话',
      requiresAuth: true
    }
  },

  // AI助手路由组
  {
    path: '/ai',
    component: Layout,
    meta: { title: 'AI助手', icon: 'ai' },
    children: [
      {
        path: '',
        redirect: '/ai/chat'
      },
      {
        path: 'chat',
        name: 'AIChat',
        component: () => import('@/views/ai/chat/index.vue'),
        meta: { 
          title: 'AI聊天',
          requiresAuth: true
        }
      },
      {
        path: 'grok',
        name: 'AIGrok',
        component: () => import('@/views/ai/grok/index.vue'),
        meta: { 
          title: 'Grok对话',
          requiresAuth: true
        }
      }
    ]
  },
  // AI会话管理路由
  {
    path: '/ai-admin',
    component: Layout,
    meta: { 
      title: 'AI会话管理', 
      icon: 'chat-dots-square',
      requiresAuth: true,
      roles: ['admin', 'super_admin']
    },
    children: [
      {
        path: 'sessions',
        name: 'AIChatSessions',
        component: () => import('@/views/ai-admin/sessions/index.vue'),
        meta: { 
          title: '会话列表',
          requiresAuth: true,
          roles: ['admin', 'super_admin']
        }
      },
      {
        path: 'detail/:sessionId',
        name: 'AIChatSessionDetail',
        component: () => import('@/views/ai-admin/sessions/detail.vue'),
        meta: { 
          title: '会话详情',
          requiresAuth: true,
          roles: ['admin', 'super_admin'],
          hideInMenu: true
        }
      },
      {
        path: 'statistics',
        name: 'AIChatStatistics',
        component: () => import('@/views/ai-admin/statistics/index.vue'),
        meta: { 
          title: '统计分析',
          requiresAuth: true,
          roles: ['admin', 'super_admin']
        }
      },
      {
        path: 'hot-topics',
        name: 'AIChatHotTopics',
        component: () => import('@/views/ai-admin/hot-topics/index.vue'),
        meta: { 
          title: '热门话题',
          requiresAuth: true,
          roles: ['admin', 'super_admin']
        }
      },
      {
        path: 'user-usage',
        name: 'AIChatUserUsage',
        component: () => import('@/views/ai-admin/user-usage/index.vue'),
        meta: { 
          title: '用户使用统计',
          requiresAuth: true,
          roles: ['admin', 'super_admin']
        }
      }
    ]
  },
  // 知识库管理路由
  {
    path: '/knowledge',
    component: Layout,
    meta: {
      title: '知识库管理',
      icon: 'collection',
      requiresAuth: true,
      permission: 'knowledge:read'
    },
    children: [
      {
        path: '',
        name: 'KnowledgeManagement',
        component: () => import('@/views/knowledge/KnowledgeManagement.vue'),
        meta: {
          title: '知识库管理',
          requiresAuth: true,
          permission: 'knowledge:read'
        }
      }
    ]
  },
  // 聊天统计路由
  {
    path: '/chat',
    component: Layout,
    meta: { title: '聊天统计', icon: 'chat' },
    children: [
      {
        path: 'statistics',
        name: 'ChatStatistics',
        component: () => import('@/views/chat/statistics.vue'),
        meta: { title: '聊天统计' }
      }
    ]
  },
  // 系统管理模块
  {
    path: '/system',
    name: 'System',
    component: Layout,
    meta: {
      title: '系统管理',
      icon: 'setting',
      requiresAuth: true
    },
    children: [
      {
        path: 'users',
        name: 'Users',
        component: () => import('../views/system/users/index.vue'),
        meta: {
          title: '用户管理',
          requiresAuth: true
        }
      },
      {
        path: 'roles',
        name: 'Roles',
        component: () => import('../views/system/roles/index.vue'),
        meta: {
          title: '角色管理',
          requiresAuth: true
        }
      },
      {
        path: 'permissions',
        name: 'Permissions',
        component: () => import('../views/system/permissions/index.vue'),
        meta: {
          title: '权限管理',
          requiresAuth: true
        }
      },
      {
        path: 'userGroups',
        name: 'UserGroups',
        component: () => import('../views/system/userGroups/index.vue'),
        meta: {
          title: '用户组管理',
          requiresAuth: true
        }
      },
      {
        path: 'permission-templates',
        component: () => import('@/views/system/permission-templates/index'),
        name: 'PermissionTemplates',
        meta: {
          title: '权限模板',
          permission: 'permission_templates:list:view'
        }
      },
      {
        path: 'user-type-change-logs',
        component: () => import('@/views/system/userTypeChangeLog/index'),
        name: 'UserTypeChangeLogs',
        meta: { 
          title: '用户类型变更日志',
          icon: 'document',
          roles: ['admin']
        }
      }
    ]
  },
  // 用户相关路由
  {
    path: '/user',
    component: Layout,
    meta: { title: '用户中心', requiresAuth: true },
    children: [
      {
        path: 'profile',
        name: 'UserProfile',
        component: () => import('@/views/user/profile/index.vue'),
        meta: { 
          title: '个人信息',
          requiresAuth: true,
          hideInMenu: true
        }
      }
    ]
  },
  {
    path: '/403',
    name: '403',
    component: () => import('../views/error/403.vue'),
    meta: {
      title: '无权限访问'
    }
  },
  {
    path: '/404',
    name: '404',
    component: () => import('../views/error/404.vue'),
    meta: {
      title: '页面不存在'
    }
  },
  // 添加微信授权错误页面
  {
    path: '/auth/error',
    name: 'AuthError',
    component: () => import('../views/auth/error.vue'),
    meta: {
      title: '授权失败'
    }
  },
  // 添加微信授权成功页面
  {
    path: '/auth/success',
    name: 'AuthSuccess',
    component: () => import('../views/auth/success.vue'),
    meta: {
      title: '授权成功'
    }
  },
  // 企业微信OAuth回调页面
  {
    path: '/auth/wecom/success',
    name: 'WecomAuthSuccess',
    component: () => import('../views/auth/wecom/success.vue'),
    meta: {
      title: '企业微信绑定成功'
    }
  },
  {
    path: '/auth/wecom/error',
    name: 'WecomAuthError',
    component: () => import('../views/auth/wecom/error.vue'),
    meta: {
      title: '企业微信绑定失败'
    }
  },
  // 办公系统路由
  officeRoutes,
  warehouseRoutes,
  // 审批管理路由已迁移到office模块中
  // approvalRoutes,
  // 数据报表路由
  {
    path: '/reports',
    component: OfficeLayout,
    redirect: '/reports/inventory/records',
    meta: {
      title: '数据报表',
      requiresAuth: true
    },
    children: [
      {
        path: 'inventory/records',
        name: 'InventoryRecordReport',
        component: () => import('@/views/reports/inventory/Records.vue'),
        meta: { title: '库存流水', requiresAuth: true, moduleType: 'OFFICE' }
      }
    ]
  },
  // 企业微信管理路由
  {
    path: '/wecom',
    component: OfficeLayout,
    redirect: '/wecom/approvals',
    meta: {
      title: '企业微信管理',
      requiresAuth: true,
      roles: ['admin', 'super_admin', 'manager']
    },
    children: [
      {
        path: 'department',
        name: 'WecomDepartment',
        component: () => import('@/views/wecom/modules/department/index.vue'),
        meta: { title: '部门管理', requiresAuth: true, moduleType: 'WECOM', roles: ['admin', 'super_admin', 'manager'] }
      },
      {
        path: 'user',
        name: 'WecomUser',
        component: () => import('@/views/wecom/modules/user/index.vue'),
        meta: { title: '用户管理', requiresAuth: true, moduleType: 'WECOM', roles: ['admin', 'super_admin', 'manager'] }
      },
      {
        path: 'message',
        name: 'WecomMessage',
        component: () => import('@/views/wecom/modules/message/index.vue'),
        meta: { title: '消息管理', requiresAuth: true, moduleType: 'WECOM', roles: ['admin', 'super_admin', 'manager'] }
      },
      {
        path: 'system/config',
        name: 'WecomConfig',
        component: () => import('@/views/wecom/system/config.vue'),
        meta: { title: '配置管理', requiresAuth: true, moduleType: 'WECOM', roles: ['admin', 'super_admin', 'manager'] }
      },
      {
        path: 'system/log',
        name: 'WecomLog',
        component: () => import('@/views/wecom/system/log.vue'),
        meta: { title: '日志查看', requiresAuth: true, moduleType: 'WECOM', roles: ['admin', 'super_admin', 'manager'] }
      },
      {
        path: 'system/sync',
        name: 'WecomSync',
        component: () => import('@/views/wecom/system/sync.vue'),
        meta: { title: '数据同步', requiresAuth: true, moduleType: 'WECOM', roles: ['admin', 'super_admin', 'manager'] }
      },
      {
        path: 'robot',
        name: 'WecomRobot',
        component: () => import('@/views/wecom/modules/robot/index.vue'),
        meta: { title: '群机器人', requiresAuth: true, moduleType: 'WECOM', roles: ['admin', 'super_admin', 'manager'] }
      },
      {
        path: 'approvals',
        name: 'WecomApprovals',
        component: () => import('@/views/wecom/approvals/index.vue'),
        meta: { title: '企业微信审批', requiresAuth: true, moduleType: 'WECOM', roles: ['admin', 'super_admin', 'manager'] }
      },
      {
        path: 'approvals-analytics',
        name: 'WecomApprovalAnalytics',
        component: () => import('@/views/wecom/approvals/analytics.vue'),
        meta: { title: '审批统计', icon: 'PieChart', requiresAuth: true, moduleType: 'WECOM', roles: ['admin', 'super_admin', 'manager'] }
      }
    ]
  },
  {
    path: '/public/shipping-orders/:id',
    name: 'PublicShippingOrder',
    component: () => import('@/views/public/ShippingOrderPublic.vue'),
    meta: {
      title: '发货订单',
      requiresAuth: false,
      hideInMenu: true
    }
  },
]

// 合并移动端路由
routes.push(...mobileRoutes)
// 调试：检查特定路由是否存在
const officeRoute = routes.find(route => route.path === '/office')
if (officeRoute && officeRoute.children) {
  console.log('Office route children:', officeRoute.children.map(child => child.path))
  
  // 检查采购申请单路由
  const purchaseRequestRoutes = officeRoute.children.filter(child => 
    child.path && child.path.startsWith('purchase-requests')
  )
  console.log('Purchase request routes:', purchaseRequestRoutes.map(r => r.path))
  
  const warehouseEditRoute = officeRoute.children.find(child => 
    child.path === 'warehouse/:id/edit'
  )
  console.log('Warehouse edit route found:', warehouseEditRoute)
  
  // 检查所有仓库相关路由
  const warehouseRoutes = officeRoute.children.filter(child => 
    child.path && child.path.startsWith('warehouse')
  )
  console.log('All warehouse routes:', warehouseRoutes.map(r => r.path))
} else {
  console.log('Office route not found or has no children')
}

// 检查数据报表路由
const reportsRoute = routes.find(route => route.path === '/reports')
if (reportsRoute && reportsRoute.children) {
  console.log('Reports route children:', reportsRoute.children.map(child => child.path))
} else {
  console.log('Reports route not found or has no children')
}
const router = createRouter({
  history: createWebHistory(''),
  routes
})

// 执行初始化设置
let tokenValidationInterval = null;

// 设置全局导航守卫
router.beforeEach(async (to, from, next) => {
  // 不使用进度条
  // NProgress.start()
  
  // 记录导航信息
  console.log('[Router] 导航到:', to.fullPath);
  console.log('[Router] 匹配的路由:', to.matched);
  console.log('[Router] 路由路径:', to.path);
  console.log('[Router] 路由参数:', to.params);
  
  // 添加调试信息：检查所有已注册的路由
  if (to.matched.length === 0) {
    console.log('[Router] 路由未匹配，检查已注册的路由:');
    const allRoutes = router.getRoutes();
    console.log('[Router] 总路由数:', allRoutes.length);
    
    // 查找 office 相关路由
    const officeRoutes = allRoutes.filter(route => route.path.includes('office'));
    console.log('[Router] Office 相关路由:', officeRoutes.map(r => r.path));
    
    // 查找仓库相关路由
    const warehouseRoutes = allRoutes.filter(route => route.path.includes('warehouse'));
    console.log('[Router] Warehouse 相关路由:', warehouseRoutes.map(r => r.path));
  }
  
  // 检查是否需要认证
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  console.log('[Router] 路由', to.fullPath, requiresAuth ? '需要认证' : '无需认证');
  
  if (requiresAuth) {
    // 获取token
    const hasToken = getToken()
    console.log('[Router] 存在token:', hasToken ? '是' : '否');
    
    if (hasToken) {
      // 检查token是否已过期
      if (isTokenExpired()) {
        console.log('[Router] token已过期，重定向到登录页');
        ElMessage.error('登录已过期，请重新登录');
        
        // 清除认证状态
        clearAllAuthState();
        
        // 重定向到登录页，并带上当前路径作为重定向参数
        next({ path: '/login', query: { redirect: to.fullPath } });
        return;
      }
      
      // 如果已经有token但正在访问登录页，重定向到首页
      if (to.path === '/login') {
        console.log('[Router] 已登录，重定向到首页');
        next({ path: '/' });
        return;
      }
      
      // 验证token有效性并同步用户信息
      try {
        const isValid = await validateToken();
        if (!isValid) {
          console.log('[Router] token无效，重定向到登录页');
          next({ path: '/login', query: { redirect: to.fullPath } });
          return;
        }
        
        // 同步用户信息
        const userStore = useUserStore();
        if (!userStore.isLoggedIn) {
          console.log('[Router] 用户信息未同步，开始同步');
          await userStore.refreshUserInfo();
        }
      } catch (error) {
        console.error('[Router] 验证token失败:', error);
      }

      // 检查权限
      // 统一改为服务端鉴权：前端不再做页面级权限码拦截
      // 若仍有个别路由声明了权限码，则按整条匹配链校验（requireAll=true），防止父/子路由丢失
      const permissionList = to.matched
        .map(record => record.meta && record.meta.permission)
        .filter(Boolean)
      if (permissionList.length > 0) {
        if (!hasPermission(permissionList, true)) {
          console.log('[Router] 权限不足，重定向到403页面。需要权限:', permissionList)
          ElMessage.error('您没有访问该页面的权限');
          next({ path: '/403' });
          return;
        }
      }

      // 角色校验：若任一匹配路由声明了 roles，则要求用户逐级满足（每级至少命中其中一个）
      const roleRequirements = to.matched
        .map(record => record.meta && record.meta.roles)
        .filter(Boolean)
      if (roleRequirements.length > 0) {
        const ok = roleRequirements.every(list => hasRole(list))
        if (!ok) {
          console.log('[Router] 角色不足，重定向到403页面。需要角色链:', roleRequirements)
          ElMessage.error('您没有访问该页面的角色权限');
          next({ path: '/403' });
          return;
        }
      }
      
      console.log('[Router] 用户已登录，允许访问');
      next();
    } else {
      console.log('[Router] 未登录，重定向到登录页');
      // 如果没有token，重定向到登录页
      next({ path: '/login', query: { redirect: to.fullPath } });
      // NProgress.done();
    }
  } else {
    // 不需要认证的路由，直接访问
    console.log('[Router] 此路由无需认证，允许访问');
    next();
  }
})

router.afterEach(() => {
  // 结束进度条
  // NProgress.done()
})

// 设置定期检查token有效性
export function setupTokenValidation(router) {
  // 清除之前可能存在的定时器
  if (tokenValidationInterval) {
    clearInterval(tokenValidationInterval);
  }
  
  // 每5分钟验证一次token有效性
  tokenValidationInterval = setInterval(async () => {
    // 只在认证路由下检查
    if (router.currentRoute.value.meta.requiresAuth) {
      try {
        const isValid = await validateToken();
        if (!isValid) {
          console.log('[TokenValidation] token无效，重定向到登录页');
          ElMessage.error('您的登录已失效，请重新登录');
          router.push('/login');
        }
      } catch (error) {
        console.error('[TokenValidation] 验证token失败:', error);
      }
    }
  }, 5 * 60 * 1000);
  
  // 设置跨标签页同步
  listenToAuthEvents(() => {
    console.log('[TokenValidation] 检测到其他标签页登出，执行本地登出');
    router.push('/login');
  });
  
  console.log('[TokenValidation] 已设置定期token验证和跨标签页同步');
}

// 应用初始化时调用一次
setupTokenValidation(router);

export default router